import axios from 'axios';
import config from 'config';

const { API_BASE_URL, STATIC_BASE_URL } = config;
const initialToken = localStorage.getItem('token')
  ? localStorage.getItem('token')
  : null;

const staticRequest = axios.create({
  baseURL: STATIC_BASE_URL,
  timeout: 15000
});

export default (function () {
  /* API를 위한 기본값 및 유틸리티 */
  let token = initialToken;

  let noAuthRequest = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true,
    timeout: 15000,
    headers: {
      'Content-Type': 'application/json'
    }
  });

  let authRequest = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true,
    timeout: 15000,
    headers: {
      'Content-Type': 'application/json',
      Authorization: token && `Bearer ${token ? token : initialToken}`
    }
  });
  //interceptors 처리 (로그인 필요 또는 서버 점검 관련 처리)
  const interceptorActions = response => {
    // 299 로그인 필요 처리
    // console.log("res", response)
    return response;
  };
  const interceptorErrorActions = error => {
    try {
      if (307 === error.response.status) {
        window.location.href = 'https://static.iex.zone/servercheck.html';
      } else {
        return Promise.reject(error);
      }
    } catch (e) {
      return Promise.reject(error);
    }
  };

  staticRequest.interceptors.response.use(
    interceptorActions,
    interceptorErrorActions
  );
  authRequest.interceptors.response.use(
    interceptorActions,
    interceptorErrorActions
  );
  noAuthRequest.interceptors.response.use(
    interceptorActions,
    interceptorErrorActions
  );

  return {
    callback(data) {
      return new Promise(function (resolve, reject) {
        const result = true;
        if (result) {
          resolve(data);
        } else {
          reject(new Error('Request is failed'));
        }
      });
    },
    recreateRequest: nextToken => {
      authRequest = axios.create({
        baseURL: API_BASE_URL,
        withCredentials: true,
        timeout: 15000,
        headers: {
          'Content-Type': 'application/json',
          Authorization: token && `Bearer ${token ? token : initialToken}`
        }
      });
      authRequest.interceptors.response.use(
        interceptorActions,
        interceptorErrorActions
      );
    },
    tokenUpdate(nextToken) {
      token = nextToken;
      this.recreateRequest(nextToken);
    },
    /* API 연결 */
    common() {
      return {
        getBranchInfo: () => noAuthRequest.get(`/main/branch_info`),
        getNoticePopup: () => staticRequest.get(`/api/check_notice_popup.json?t=${new Date().getTime()}`),
        getServiceAvailable: () => noAuthRequest.get(`/service/info`),
        sendErrorLog: body => noAuthRequest.post(`//fslog.iex.zone/`, body)
      };
    },
    accounts() {
      return {
        checkIsLoginRecaptchaRequired: () => noAuthRequest.get(`/join/login_recaptcha`),
        login: ({ email, password, googleRecaptchaCode }) => noAuthRequest.post(`/join/sign_in`, {
          email,
          password,
          googleRecaptchaCode
        }),
        logout: () => authRequest.get(`/join/sign_out`),
        logoutAll: () => authRequest.get(`/join/sign_out/all`),
        loginWithOTP: ({ otpCode, session_token }) => noAuthRequest.post(`/join/otp_sign_in`, {
          otpCode,
          session_token
        }),
        checkToken: () => authRequest.get(`/account/token_check`),
        requestEmailVerificationForSecurityLevel: () => authRequest.get(`/account/email_verification`),
        checkEmailVerificationForSecurityLevel: ({ email, email_code }) => authRequest.post(`/account/email_verification_check`, {
          email,
          email_code,
          type: 'EMAILCERT'
        }),
        requestEmailVerification: ({ email, name, type }) => noAuthRequest.post(`/join/email_verification`, {
          email,
          name,
          type
        }),
        checkEmailVerification: ({ email, email_code, type }) => noAuthRequest.post(`/join/email_verification_check`, {
          email,
          email_code,
          type
        }),
        requestUserVerification: () => noAuthRequest.get(`/join/hp_name_check`),
        requestHpVerification: ({ email, hp }) => noAuthRequest.get(`/join/hp_verification`, {
          params: {
            email,
            hp
          }
        }),
        checkHpVerification: ({ email, hp, code }) => noAuthRequest.post(`/join/hp_verification_check`, {
          email,
          hp,
          code
        }),
        requestPwHpVerification: ({ hp }) => noAuthRequest.get(`/join/change_pass_send_mail`, {
          params: {
            hp
          }
        }),
        checkPwHpVerification: ({ hp, hpVerifyCode }) => noAuthRequest.post(`/join/pw_hp_verification_check`, {
          hp,
          hpVerifyCode
        }),
        requestHpVerificationMember: () => authRequest.get(`/account/hp_name_check`),
        requestHpVerificationWithdraw: ({ email, hp }) => authRequest.get(`/account/hp_verification`, {
          params: {
            email,
            hp
          }
        }),
        checkHpVerificationMember: ({ email, hp, code }) => authRequest.post(`/account/hp_verification_check`, {
          email,
          hp,
          code
        }),
        signUp: data => noAuthRequest.post(`/join/sign_up`, data),
        findPassword: ({ hp, hpVerifyCode }) => noAuthRequest.post(`/join/find_pwd`, {
          hp,
          hpVerifyCode
        }),
        getMyInfo: () => authRequest.get(`/account/signinfo`, { data: {} }),
        updateMyInfo: data => authRequest.post(`/join/sign_update`, data),
        getMyBalance: () => authRequest.get(`/balance/trade_balance`, { data: {} }),
        requestFindPw: ({ email }) => noAuthRequest.post(`/join/change_pass_send_mail`, { email }),
        changePw: ({ password, new_password, new_password_check, session_id }) => noAuthRequest.post(`/join/change_pass_submit`, {
          password,
          new_password,
          new_password_check,
          session_id
        }),
        changePwCheck: ({ code }) => noAuthRequest.get(`/join/change_pass_page_access_check/${code}`),
        requestMobileVerification: ({ mb_hp }) => noAuthRequest.post(`/account/authentication_number`, {
          mb_hp
        }),
        checkMobileVerification: ({ mb_hp, mb_key }) => noAuthRequest.post(`/account/authentication_number_check`, {
          mb_hp,
          mb_key
        }),
      };
    },
    market() {
      return {
        getMarketList: targetMarket => noAuthRequest.get(`/exchangerInfo/${targetMarket ? targetMarket : 'market_config'}`),
        getCoinInfo: coinSymbol => staticRequest.get(`/api/coininfo/${coinSymbol.toUpperCase()}.json?t=${new Date().getTime()}`),
        getVolume: () => authRequest.get(`/trade/trade_total`),
        order: ({ channel, currency, od_action, od_market_price, od_pay_status, od_temp_coin, od_total_cost }) => authRequest.post(`/trade/${od_action}`, {
          channel,
          currency,
          od_action,
          od_market_price,
          od_pay_status,
          od_temp_coin,
          od_total_cost
        }),
        // 주문취소
        cancelOrder: ({ ac_type, currency, od_id, od_pay_status, targetMarket }) => authRequest.post(`/trade/order_cancel/${targetMarket}/${currency}`, {
          ac_type,
          currency,
          od_id,
          od_pay_status
        }),
        // 전체주문취소( 사용안함 )
        cancelOrderAll: ({ currency, od_action }) => authRequest.post(`/trade/order_cancel/usdt/${currency}`, {
          od_action
        }),
        getContributionCash: () => authRequest.get(`/account/contribution_date`),
        getMiningStatus: () => authRequest.get(`/trade/full_day_disclosure`),
        // 체결내역
        getMyConfirmedOrder: ({ coinSymbol, page, limit, start_date, end_date, od_action, targetMarket }) => authRequest.get(`/trade/trade_complete_list/${targetMarket}/${coinSymbol}`, {
          params: {
            page,
            limit,
            start_date,
            end_date,
            od_action
          }
        }),
        // 미체결내역
        getMyOutstandingOrder: ({ coinSymbol, market, page, limit, start_date, end_date, od_action, targetMarket }) => authRequest.get(`/trade/open_order_list/${targetMarket}/${coinSymbol}`, {
          params: {
            page,
            limit,
            start_date,
            end_date,
            od_action,
            market
          }
        }),
        getChartData: () => authRequest.get(``)
      };
    },
    wallet() {
      return {
        getConvertPrice: () => authRequest.get("/exchangerInfo/exchange_price"),
        // 환전
        requestConvertUsdt: ({ po_type, market_price, coin_amount, exchange_amount }) => authRequest.post(`/wallet/exchange_apply`, {
          po_type,
          market_price,
          coin_amount,
          exchange_amount,
        }),
        getConvertHistory: ({ page, limit, start_date, end_date, day }) => authRequest.get(`/wallet/exchange_list`, {
          params: {
            page,
            limit,
            start_date,
            end_date,
            day
          }
        }),
        getWalletList: () => noAuthRequest.get(`/exchangerInfo/wallet_config`),
        getLockInfo: () => authRequest.get(`/wallet/lock_info`),
        getCASHDepositAddress: () => authRequest.get(`/wallet/branch_bank_account`),
        requestCASHBankDeposit: ({ od_bank_account, od_bank_name, od_temp_bank, od_rate, od_temp_cash }) => authRequest.post(`wallet/cash_deposit_apply`, {
          od_bank_account,
          od_bank_name,
          od_temp_bank,
          od_rate,
          od_temp_cash
        }),
        createOrGetMyAddress: ({ currency, check }) => authRequest.post(`/wallet/address/create`, {
          currency,
          check
        }),
        requestCASHBankWithdraw: ({ od_bank_account, od_bank_holder, od_bank_name, od_hp_cert_num, od_otp, od_receipt_amount, od_temp_amount, od_temp_cash, od_fee, od_rate }) => authRequest.post(`wallet/cash_withdrawal_apply`, {
          od_bank_account,
          od_bank_holder,
          od_bank_name,
          od_hp_cert_num,
          od_otp,
          od_receipt_amount,
          od_temp_amount,
          od_temp_cash,
          od_rate,
          od_fee,
          type: 'regist'
        }),
        requestCoinWithdraw: ({ od_addr, dtag, od_fee, od_otp, od_hp_addr, od_temp_amount, po_type }) => authRequest.post(`/wallet/coin_withdrawal_apply`, {
          od_addr,
          od_fee,
          od_otp,
          od_hp_addr,
          od_temp_amount,
          po_type,
          dtag,
          od_sendto: '',
          od_status: 'WAIT'
        }),
        checkWithdrawAddress: ({ currency, addr }) => authRequest.post(`/wallet/address_check`, {
          currency,
          addr
        }),
        getCoinDepositHistory: ({ coinSymbol, page, limit, start_date, end_date, day }) => authRequest.get(`/wallet/deposit_list/${coinSymbol}`, {
          params: {
            page,
            limit,
            start_date,
            end_date,
            day
          }
        }),
        getCoinWithdrawHistory: ({ coinSymbol, page, limit, start_date, end_date, day }) => authRequest.get(`/wallet/withdrawal_list/${coinSymbol}`, {
          params: {
            page,
            limit,
            start_date,
            end_date,
            day
          }
        }),
        getCoinDepositWaitHistory: ({ coinSymbol, page, limit, start_date, end_date, day }) => authRequest.get(`/wallet/deposit_wait_list/${coinSymbol}`, {
          params: {
            page,
            limit,
            start_date,
            end_date,
            day
          }
        }),
        getUIOAirdropHistory: ({ page, limit, start_date, end_date }) => authRequest.get(`/account/coinHistory/uio`, {
          params: {
            page,
            limit,
            start_date,
            end_date
          }
        }),
        getCoinAirdropHistory: ({ coinSymbol, page, limit, start_date, end_date, day }) => authRequest.get(`/wallet/payment_list/${coinSymbol}`, {
          params: {
            page,
            limit,
            start_date,
            end_date,
            day
          }
        }),
        getCoinStakingHistory: ({ coinSymbol, page, limit, start_date, end_date, day }) => authRequest.get(`/wallet/staking_list/${coinSymbol}`, {
          params: {
            page,
            limit,
            start_date,
            end_date,
            day
          }
        }),
        getCoinAirdropLockHistory: ({ coinSymbol, page, limit, start_date, end_date, day }) => authRequest.get(`/wallet/airdrop_list/${coinSymbol}`, {
          params: {
            page,
            limit,
            start_date,
            end_date,
            day
          }
        }),
        redirectToCheckTxidPage: ({ coinSymbol, txid }) => {
          window.open(`${API_BASE_URL}/wallet/coin_site_redirect/${coinSymbol}/${txid}`);
        },
        getCoinDepositAlert: coinSymbol => staticRequest.get(`/api/wallet_alert/deposit/${coinSymbol.toUpperCase()}.json?t=${new Date().getTime()}`),
        getCoinWithdrawAlert: coinSymbol => staticRequest.get(`/api/wallet_alert/withdrawal/${coinSymbol.toUpperCase()}.json?t=${new Date().getTime()}`),
        getWithdrawalLimit: coin => authRequest.get(`/wallet/walletlimit/${coin}`),
        getLockedAirdropCoin: coinSymbol => authRequest.get(`/wallet/airdroplock/${coinSymbol}`),
        requestSwap: ({ evtIdx, swapAmount }) => authRequest.post(`wallet/requestSwap`, {
          evtIdx,
          swapAmount
        }),
        getSwapInfo: ({ evtIdx }) => authRequest.post(`wallet/swapinfo`, {
          evtIdx
        }),
        requestCashDepositCancel: odId => authRequest.get(`/wallet/bankDeposits/delete/${odId}`),
        getSwapList: ({ coinSymbol, page, limit, start_date, end_date, }) => authRequest.post(`wallet/swap_list/${coinSymbol}`, {
          page,
          limit,
          start_date,
          end_date,
        }),
      };
    },
    cscenter() {
      return {
        getNoticeList: (start, limit, language) => authRequest.get(`/cscenter/notice`, {
          params: {
            page: start,
            language,
            limit
          }
        }),
        getNoticeDetail: (idx, language) => authRequest.get(`/cscenter/notice/${idx}`, {
          params: {
            language
          }
        }),
        getFaqCategory: () => noAuthRequest.get(`/cscenter/faqCateg`),
        getFaqContents: ({ categ, page, limit }) => noAuthRequest.get(`/cscenter/faq`, {
          params: {
            categ,
            page,
            limit
          }
        }),
        getInquiryList: (start, limit) => authRequest.get(`/cscenter/inquiry`, {
          params: {
            page: start,
            limit
          }
        }),
        getInquiryDetail: idx => authRequest.get(`/cscenter/inquiry/${idx}`),
        submitInquiry: body => authRequest.post(`/cscenter/notice_insert`, body, {
          headers: {
            'content-type': 'application/json'
          }
        }),
        getDepositAndWithdrawLimit: () => noAuthRequest.get(`/exchangerInfo/level_limit`)
      };
    },
    chart() {
      return {
        getTime: () => authRequest.get(`/chart/time`),
        // 차트
        getChart: ({ quoteSymbol, coinSymbol, resolution, from, to }) => authRequest.get(`/chart/${quoteSymbol}/symbol-${coinSymbol}/resolution-5/from-${from}/to-${to}`),
        // 미니차트
        getAllCharts: () => noAuthRequest.get(`/chart/main_mini_chart_data/Usdt/resolution-5/from-1562759584/to-1562845984`)
      };
    },
    mypage() {
      return {
        requestUpdateSession: () => authRequest.get(`account/level_check`),
        cancelAccountCheck: () => authRequest.get(`/join/leave_check`, { params: {} }),
        cancelAccount: () => authRequest.get(`/join/leave`, { params: {} }),
        getConnectionInfoList: (start, limit) => authRequest.get(`/account/login_history`, {
          params: {
            page: start,
            limit
          }
        }),
        getContributionCashList: (start, limit, dates) => authRequest.get(`/account/contribution_cache_list`, {
          params: {
            page: start,
            limit,
            start_date: dates[0],
            end_date: dates[1]
          }
        }),
        getRecommenderList: (start, limit) => authRequest.get(`/account/recommender`, {
          params: {
            page: start,
            limit
          }
        }),
        requestKYC: formData => authRequest.post(`/account/kyc_apply`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }),
        verifyBankAccount: ({ bank, bank_account_num }) => authRequest.post(`/account/bank_account`, {
          bank,
          bank_account_num
        }),
        requestNewOtp: () => authRequest.get(`/account/otp_qr_code_create`),
        requestNewOtpUse: ({ ac_type, email, password, g_otp }) => authRequest.post(`/account/otp_status`, {
          ac_type,
          email,
          password,
          g_otp
        }),
        deleteOtp: ({ ac_type, g_otp }) => authRequest.post(`/account/otp_status`, {
          ac_type,
          g_otp
        }),
        updateOtp: ({ ac_type, use_for_login, use_for_deposit_withdraw, g_otp }) => authRequest.post(`/account/otp_status`, {
          ac_type,
          use_for_login,
          use_for_deposit_withdraw,
          g_otp
        }),
        checkOtp: ({ g_otp, from }) => authRequest.post(`/account/otp_status`, {
          ac_type: 'check',
          g_otp,
          from,
        }),
        requestLv4: () => authRequest.get(`/account/withdrawal_limit_increase_apply`),
        checkKyc: () => authRequest.get(`/account/kyc_check`),
        checkIsLv4: () => authRequest.get(`/account/withdrawal_limit_increase_check`)
      };
    },
    swap() {
      return {
        getSwapList: ({ po_type, swap_type, page, limit, start_date, end_date, }) => authRequest.post(`/swap/swap_list`, {
          po_type,
          swap_type,
          page,
          limit,
          start_date,
          end_date,
        }),
        getSwapEventList: ({ swap_type }) => authRequest.post(`/swap/swap_event_list`, {
          swap_type
        }),
        getSwapEventDetail: ({ swap_type, swap_idx, }) => authRequest.post(`/swap/swap_event_detail`, {
          swap_type,
          swap_idx,
        }),
        getSwap: ({ swap_idx, swap_point, }) => authRequest.post(`/swap/swap_request`, {
          swap_idx,
          swap_point,
        }),
        getSwapEventCoinList: ({ swap_type, }) => authRequest.post(`/swap/swap_event_coin_list`, {
          swap_type,
        }),
        setSwapCancel: ({ idx, }) => authRequest.post(`/swap/swap_cancel`, {
          idx,
        }),
        getRequestOverlap: ({ swap_idx, }) => authRequest.post(`/swap/request_overlap`, {
          swap_idx,
        }),
      }
    }
  };
})();
